import { useCallback, useState } from 'react';
import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { useTranslation } from 'core/i18n/i18n';
import { SettingsFullWidthContentLayout } from 'modules/settings/components';
import { SettingsTableHeaderOptionsComponent } from 'modules/settings/components/settings-table-header-options/settings-table-header-options.component';
import { OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.constants';
import { SettingsOperatorCustomNotificationsListComponent } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications-list';
import { useOperatorCustomNotificationsListQuery } from 'modules/settings/pods/settings-operator-custom-notifications/service/hooks/use-operator-custom-notifications-list.hook';
import { useOperatorCustomNotificationRows } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notification-rows.hook';
import { useOperatorCustomNotificationsTableConfig } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notifications-table-config.hook';
import { useOperatorCustomNotificationsSort } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notifications-sort.hook';
import { useOperatorCustomNotificationsSelection } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notifications-selection.hook';
import { OperatorCustomNotificationsBulkActionsContainer } from 'modules/settings/pods/settings-operator-custom-notifications/components/operator-custom-notifications-bulk-actions';
import {
    OperatorCustomNotificationEditCommandComponent,
    OperatorCustomNotificationRowOptionsComponent,
} from 'modules/settings/pods/settings-operator-custom-notifications/components/operator-custom-notification-row-options';
import { OperatorCustomNotificationRow } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.model';
import { useOperatorCustomNotificationsConfirmationModal } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notifications-confirmation-modal.hook';
import { useOperatorCustomNotificationsNavigation } from 'modules/settings/pods/settings-operator-custom-notifications/hooks/use-operator-custom-notifications-navigation.hook';

export const SettingsOperatorCustomNotificationsContainer: React.FC = () => {
    const { t } = useTranslation();
    const [showOnlyActive, setShowOnlyActive] = useState<boolean>(true);

    const { notifications, isLoading, hasFailedToLoad } = useOperatorCustomNotificationsListQuery(!showOnlyActive);
    const rows = useOperatorCustomNotificationRows(notifications);
    const { sortedRows, defaultSort, onSortOrderChange } = useOperatorCustomNotificationsSort(rows);
    const config = useOperatorCustomNotificationsTableConfig();
    const selection = useOperatorCustomNotificationsSelection(sortedRows);
    const { clearSelection } = selection;
    const { navigateToCreate, navigateToEdit } = useOperatorCustomNotificationsNavigation();
    const { showConfirmation } = useOperatorCustomNotificationsConfirmationModal(clearSelection);

    const handleShowOnlyActiveChange = useCallback(
        (nextShowOnlyActive: boolean) => {
            setShowOnlyActive(nextShowOnlyActive);
            clearSelection();
        },
        [clearSelection]
    );

    const getRowOptions = (notification: OperatorCustomNotificationRow) => [
        <OperatorCustomNotificationEditCommandComponent
            key={`operator-custom-notification-edit-${notification.id}`}
            notificationId={notification.id}
            notificationName={notification.notificationName}
            onEdit={navigateToEdit}
        />,
        <OperatorCustomNotificationRowOptionsComponent
            key={`operator-custom-notification-options-${notification.id}`}
            notification={notification}
            onRequestAction={(action, requested) => showConfirmation(action, [requested])}
        />,
    ];

    return (
        <SettingsFullWidthContentLayout
            title={t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.title`)}
            description={t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.description`)}
            actionsContent={
                <PocButton variant="secondary" onClick={navigateToCreate}>
                    {t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.actions.add`)}
                </PocButton>
            }
            extraContent={
                <OperatorCustomNotificationsBulkActionsContainer
                    selectedRows={selection.selectedRows}
                    areAllActive={selection.areAllActive}
                    areAllInactive={selection.areAllInactive}
                    onClearSelection={clearSelection}
                    onRequestAction={showConfirmation}
                />
            }
        >
            <SettingsOperatorCustomNotificationsListComponent
                rows={sortedRows}
                config={config}
                isLoading={isLoading}
                hasFailedToLoad={hasFailedToLoad}
                defaultSort={defaultSort}
                onSortOrderChange={onSortOrderChange}
                selectedRows={selection.selectedRows}
                selectedRowIds={selection.selectedRowIds}
                headerCheckboxProps={selection.headerCheckboxProps}
                onSelectRow={selection.handleSelectRow}
                onClickRow={navigateToEdit}
                rowOptions={getRowOptions}
                optionComponents={
                    <SettingsTableHeaderOptionsComponent
                        onShowOnlyActiveChange={handleShowOnlyActiveChange}
                        showOnlyActive={showOnlyActive}
                        text={t(`${OPERATOR_CUSTOM_NOTIFICATIONS_I18N_PREFIX}.list.actions.show-inactive`)}
                    />
                }
            />
        </SettingsFullWidthContentLayout>
    );
};
